import Component from 'vue-class-component';
import EdUserMenu from '@/auth/components/ed-user-menu/ed-user-menu.vue';
import EdNav from '../ed-nav/ed-nav.vue';
import EdNavMobile from '../ed-nav-mobile/ed-nav-mobile.vue';
import EdNavMeetings from '@/core/components/ed-nav-meetings/ed-nav-meetings.vue';
import EdNavMeetingsMobile from '@/core/components/ed-nav-meetings-mobile/ed-nav-meetings-mobile.vue';
import EdNavMeetingsTools from '@/core/components/ed-nav-meetings-tools/ed-nav-meetings-tools.vue';
import EdHeaderSidebarMenu from '../ed-header-sidebar-menu/ed-header-sidebar-menu.vue';
import EdLanguage from '../ed-language/ed-language.vue';
import { Prop } from 'vue-property-decorator';
import { Mixins } from 'vue-property-decorator';
import { CORE_ROUTES } from '@/core/core.router';
import KEYS from '@/values/keys';
import NavRedirectMixin from '@/core/mixins/nav-redirect.mixin';
import { ROLES } from '@/auth/enums/auth.enum';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import { PURCHASE_TYPES, TEMPLATE_SCOPE } from '@/course-templates/values/course-templates.values';
import { APP_API, APP_SOCKET_CLASS_MESSAGES_TYPE, LOAD_CLASS_MESSAGES, LOAD_ROOM_CLASS_MESSAGES, LOAD_TEMPLATE_MESSAGES } from '@/core/values/app.values';
import { Capacitor } from '@capacitor/core';
import { BackupFileServices } from '@/core/services/backup.file.services';
import SyncWorker from 'worker-loader!@/workers/sync-worker.ts';
import { LocalStorageService } from '@/services/local-storage.service';
import { AUTH_STORAGE_KEYS } from '@/auth/values/auth.values';
import { WORKER_EVENT_BUS } from '@/core/enums/worker.enum';
import { BUILDER_STATUS } from '@/courses/values/courses.values';
import { EdCallsComponent } from '@/components/calls/ed-calls/ed-calls.component';
import { _CLASSES_STORE } from '@/classes/values/classes.values';
import { EVENT_TYPES, SOCKET_EVENTS } from '@/content/values/ed-presentation-mode.values';
import edPresentationModeFactory from '@/content/factories/ed-presentation-mode.factory';
import { _MEETINGS_STORE } from '@/meetings/values/meetings.values';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { MY_CONTENT_EVENTS } from '@/content/values/events';

const DEFAULT_DELAY = 30000;  // Default delay time to start the worker (30 seconds)
const INMEDIATELY_DELAY = 1000; //One second delay to start the worker immediately
@Component({
  name: 'ed-header-in-session',
  components: {
    EdSearch: () => import(/* EdSearch */'@/core/components/ed-search/ed-search.vue'),
    EdUserMode: () => import(/* EdUserMode */'@/core/components/ed-user-mode/ed-user-mode.component.vue'),
    EdNav,
    EdNavMobile,
    EdUserMenu,
    EdHeaderSidebarMenu,
    EdLanguage,
    EdNavMeetings,
    EdNavMeetingsMobile,
    EdNavMeetingsTools
  }
})
export default class EdHeaderInSession extends Mixins(NavRedirectMixin) {

  @Prop() readonly sidebarMenu: boolean;

  private timerId = null;
  private worker: any;
  private workerIsRunning: boolean = false;
  private workerRunAsap: boolean = false;
  private callsComponentInstance : EdCallsComponent | null = null;

  get userRoles(): string[] {
    return this.$store.state.AuthStore.userRoles;
  }

  get currentMeetingData() {
    return this.$store.state.MeetingsStore.currentMeetingData;
  }

  get isOnCall() : boolean {
    return this.$store.state.CallsStore.isOnCall;
  }

  get isMeeting() {
    return (this.currentMeetingData.classId > 0 && this.currentMeetingData.meetingId > 0);
  }

  get isPublisherWithToggle() : boolean {
    return (this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER && this.isMeeting && !this.isPresentationModeGuestTeacher);
  }

  get isUserModeShown() : boolean {
    return this.userRoles.includes(ROLES.EDUAC_STUDENT);
  }

  get isPresentationModeGuestTeacher() : boolean {
    return this.$store.state.MeetingsStore.isPresentationModeGuestTeacher;
  }

  public onClickBugButton() {
    BackupFileServices.sendUserInfoFromBugButton();
  }

  get gradeLevelSelected() {
    return this.$store.state.CourseTemplateStore.templatesGradeLevelSelected;
  }

  get canReload() {
    return Capacitor.isNativePlatform();
  }

  get callsComponent(){
    return this.callsComponentInstance;  
  }

  get isPublisher(): boolean {
    return this.$Auth.Store.isPublisher;
  }

  reloadWindow() {
    location.reload();
  }

  async loadTemplates(message : WebSocketMessageResponse) {
    if(message.userType) {
      message.userType.split(',').forEach(async type => {
        if(type == PURCHASE_TYPES.PURCHASE) {
          this.$CourseTemplates.Store.initMyContentTemplates(PURCHASE_TYPES.PURCHASE);
          await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.PURCHASE, this.gradeLevelSelected);
        } else if(type == PURCHASE_TYPES.SHARED_WITH) {
          this.$CourseTemplates.Store.initMyContentTemplates(PURCHASE_TYPES.SHARED_WITH);
          await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.SHARED_WITH, this.gradeLevelSelected);
        } else if(type == PURCHASE_TYPES.OWNER) {
          this.$CourseTemplates.Store.initMyContentTemplates(PURCHASE_TYPES.OWNER);
          await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.OWNER, this.gradeLevelSelected);
        }
      });
    } else {
      this.$CourseTemplates.Store.initMyContentTemplates(TEMPLATE_SCOPE.PUBLIC);
      await this.$CourseTemplates.Store.getCourseTemplates(TEMPLATE_SCOPE.PUBLIC);
      this.$CourseTemplates.Store.setFirstTimeContentTemplate(TEMPLATE_SCOPE.PUBLIC, true);
      this.$CourseTemplates.Store.initMyContentTemplates(TEMPLATE_SCOPE.ORGANIZATION);
      await this.$CourseTemplates.Store.getCourseTemplates(TEMPLATE_SCOPE.ORGANIZATION);
      this.$CourseTemplates.Store.setFirstTimeContentTemplate(TEMPLATE_SCOPE.ORGANIZATION, true);
      this.$CourseTemplates.Store.initMyContentTemplates(PURCHASE_TYPES.OWNER);
      await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.OWNER, this.gradeLevelSelected);
    }
  }

  /**
   * Instance and start worker 
   */
  initWorker() {
    if(!this.worker) {
      this.worker = new SyncWorker();
      this.configureWorkerEvents();
      this.startWorker();
    }
  }

  /**
   * Start sync worker ensuring it was previoulsy innitialized and is not already running
   */
  startWorker() {
    if(!this.worker || this.workerIsRunning) 
      return;

    this.workerIsRunning = true;
    const accessToken = LocalStorageService.getItem(AUTH_STORAGE_KEYS.ED_JWT);
    this.worker.postMessage({
      type: 'start',
      serviceUrl: APP_API.HEALTH_API,
      accessToken: accessToken
    });
  }

  /**
   * Request to start worker (if the worker is already running, set the next execution time to 5 seconds)
   * This function is called when an important change is made that requires the worker to run immediately
   * (e.g., saving a builder). If the worker is already running, it will not interrupt its current process;
   * instead, it will set the next execution time to INMEDIATELY_DELAY seconds to ensure that the changes are processed promptly.
   * If the worker is not running, it will start it after a short delay of INMEDIATELY_DELAY seconds.
   * This approach helps to manage the worker's workload efficiently without overwhelming it with immediate restarts.
   */
  requestStartWorker() {
    if(this.workerIsRunning){
        this.workerRunAsap = true; 
    } else{
      this.clearWorkerTimer();
      this.timerId = setTimeout(this.startWorker, INMEDIATELY_DELAY);
    }
  }

  /**
   * end timer
   */
  clearWorkerTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * Stop sync worker
   */
  finishWorker() {
    if(this.worker) {
      this.worker.postMessage({
        type: 'stop'
      });
      this.worker.terminate();
      this.worker = null;
    }
  }

  /**
   * Configure the listening messages from worker
   */
  configureWorkerEvents() {
    this.worker.onmessage = (event: MessageEvent<{ builderId: string; syncBuilderStatus?: string; publishBuilder?: boolean; initTimer?: boolean; endTimer?: boolean}>) => {
      if (event.data.syncBuilderStatus) {
        this.$edEventBus.$emit(WORKER_EVENT_BUS.SYNC_BUILDER_STATUS, {
          builderId: event.data.builderId,
          status: event.data.syncBuilderStatus
        });
      }
  
      if (event.data.publishBuilder) {
        this.$edEventBus.$emit(WORKER_EVENT_BUS.BUILDER_STATUS, {
          builderId: event.data.builderId,
          status: BUILDER_STATUS.DONE
        });
      }
      
      //The worker finished processing but there are pendings to process, prepare to start again
      if (event.data.initTimer) {  
        this.clearWorkerTimer();
        this.timerId = setTimeout(this.startWorker, this.workerRunAsap ? INMEDIATELY_DELAY : DEFAULT_DELAY);
        this.workerRunAsap = false;
        this.workerIsRunning = false;
      }

       //The worker finished processing and there are no more pendings to process
      if (event.data.endTimer) {  
        if(this.workerRunAsap){     //If during the worker process a new request to run ASAP was received, start the worker again soon
          this.clearWorkerTimer();
          this.timerId = setTimeout(this.startWorker, INMEDIATELY_DELAY);
        }
        this.workerRunAsap = false;
        this.workerIsRunning = false;
      }
    };
  }
  
  async loadClassMessages(message : WebSocketMessageResponse) {
    this.$edEventBus.$emit(MY_CONTENT_EVENTS.RELOAD_MENU, {inProcess: true});
    await this.$store.dispatch(_CLASSES_STORE.ACTIONS.GET_ALL_CLASSES_A);
    await this.loadRoomClassMessages(message);
       
    if(message.messageType === APP_SOCKET_CLASS_MESSAGES_TYPE.CLASS_ADDED) {
      this.$EdSocket.emit(SOCKET_EVENTS.connect, edPresentationModeFactory.createWebSocketMessageRequest(EVENT_TYPES.userConnect, message.classId));
      if(!this.isPublisher && message.topicId) {
        this.$EdSocket.emit(SOCKET_EVENTS.connectStudent, edPresentationModeFactory.createWebSocketMessageRequest(EVENT_TYPES.userConnect, message.topicId.toString()));
      }
    } else if(message.messageType === APP_SOCKET_CLASS_MESSAGES_TYPE.CLASS_DELETED && !this.isPublisher && message.topicId) {
      this.$EdSocket.emit(SOCKET_EVENTS.disconnect,  edPresentationModeFactory.createWebSocketMessageRequest(EVENT_TYPES.disconnect, message.classId));
      this.$EdSocket.emit(SOCKET_EVENTS.disconnect,  edPresentationModeFactory.createWebSocketMessageRequest(EVENT_TYPES.studentDisconnect, message.topicId.toString()));
    }
    this.$EdToast.create({ 
      msg:  `${this.$t('classes.info.userClassesUpdatedWarning')}`,
      type: TOAST_TYPES.WARNING,
      top: '3rem',
      timeout: 5000
    });
    this.$edEventBus.$emit(MY_CONTENT_EVENTS.RELOAD_MENU, {inProcess: false});
  }
  
  async loadRoomClassMessages(message : WebSocketMessageResponse) {
    await this.$store.dispatch(_MEETINGS_STORE.ACTIONS.GET_ALL_ROOM_CLASSES_A);
  }

  mounted() {
    this.$edEventBus.$on(LOAD_TEMPLATE_MESSAGES, this.loadTemplates);
    this.initWorker();
    this.$edEventBus.$on(WORKER_EVENT_BUS.SEND_SYNC_WORKER_MESSAGE, this.requestStartWorker);
    this.$edEventBus.$on(LOAD_ROOM_CLASS_MESSAGES, this.loadRoomClassMessages);
    this.$edEventBus.$on(LOAD_CLASS_MESSAGES, this.loadClassMessages);
    this.callsComponentInstance = this.$refs.callsComponent as EdCallsComponent;
  }

  beforeDestroy() {
    this.$edEventBus.$off(LOAD_TEMPLATE_MESSAGES, this.loadTemplates);
    this.$edEventBus.$off(WORKER_EVENT_BUS.SEND_SYNC_WORKER_MESSAGE, this.requestStartWorker);
    this.$edEventBus.$off(LOAD_ROOM_CLASS_MESSAGES, this.loadRoomClassMessages);
    this.$edEventBus.$off(LOAD_CLASS_MESSAGES, this.loadClassMessages);
    this.finishWorker();
  }

}
